import { useEffect } from "react";
import { useSelector } from "react-redux";
import { round } from "lodash";
import { Grid } from "@financeable-com-au/financeable-ui";
// @ts-expect-error: ignoring as it is existing js file
import { loanDetailsSelector } from "@/store/slices/applicationFormSlice";
import { type Fee, FeeFrequency, InArrears, RepaymentFrequency } from "@/store/slices/types/applicationFormSlice";
import { useLenderCardContext } from "@/providers/LenderCardContextProvider";
import {
  formatFeesForSaveQuote,
  getRepaymentsByFrequency,
  getTotalMonthlyPaymentProcessingFee,
} from "@/utils/lenderUtils";
import { FeeFields } from "@/components/LenderProductCard/LenderCardDetailed/QuoteCalculatorExpanded/FeeFields";
import { useCalculateApplication } from "@/hooks";
import { ManualQuoteFields } from "../ManualQuoteFields";

const QuoteCalculator = () => {
  const { lenderCardCustom, quoteCalculatorFormik } = useLenderCardContext();
  const { calculateQuoteRepayments } = useCalculateApplication();
  const loanDetails = useSelector(loanDetailsSelector);

  useEffect(() => {
    // @ts-expect-error: ignoring as it is existing js file
    if (loanDetails?._id) {
      quoteCalculatorFormik?.setValues({
        ...quoteCalculatorFormik?.values,
        // @ts-expect-error: ignoring as it is existing js file
        lender: loanDetails?.lender ?? "",
        effectiveRate: round(lenderCardCustom?.rate?.effective.value ?? 0, 2),
        repayments: round(lenderCardCustom?.repayments?.primary.value ?? 0, 2),
      });
    }
  }, [loanDetails]);

  const saveQuote = ({ actionFor, value }: { actionFor: string; value?: string | Fee[] | FeeFrequency }) => {
    const { rate, repayments, fees, repaymentFrequency, financeAmount, brokerage } = lenderCardCustom;
    if (quoteCalculatorFormik?.isValid) {
      switch (actionFor) {
        case "rate":
          quoteCalculatorFormik.setValues({
            ...quoteCalculatorFormik.values,
            rate: round(rate.default.value ?? 0, 2),
            effectiveRate: round(rate.effective.value ?? 0, 2),
            repayments: round(repayments.primary.value ?? 0, 2),
          });
          break;
        case "inArrears":
          const monthlyRepayments = calculateQuoteRepayments({
            financeAmount: financeAmount?.value ?? 0,
            // @ts-expect-error: ignoring as it is existing js file
            balloon: loanDetails?.balloon ?? 0,
            rate: round(rate.default.value ?? 0, 2),
            isInArrears: value === InArrears.Yes ? true : false,
          });
          const monthlyPaymentProcessingFee = quoteCalculatorFormik?.values?.fees?.find(
            (fee) => fee.key === "paymentProcessingFee",
          )?.value
            ? getTotalMonthlyPaymentProcessingFee({ fees: quoteCalculatorFormik?.values?.fees })
            : 0;

          const monthlyRepaymentsWithMonthlyFees =
            monthlyRepayments + (fees?.totalMonthly?.value ?? 0) + monthlyPaymentProcessingFee;

          const updatedRepayments = getRepaymentsByFrequency({
            monthlyRepayments: monthlyRepaymentsWithMonthlyFees,
            repaymentFrequency,
          });
          quoteCalculatorFormik.setValues({
            ...quoteCalculatorFormik.values,
            inArrears: value as InArrears,
            repayments: round(updatedRepayments ?? 0, 2),
          });
          break;
        case "brokerage":
          quoteCalculatorFormik.setValues({
            ...quoteCalculatorFormik.values,
            brokerage: round(brokerage.percentage.value ?? 0, 2),
            repayments: round(repayments.primary.value ?? 0, 2),
            effectiveRate: round(rate.effective.value ?? 0, 2),
          });
          break;
        case "fees":
          quoteCalculatorFormik.setValues({
            ...quoteCalculatorFormik.values,
            fees: formatFeesForSaveQuote(fees.application).map((fee) => ({
              ...fee,
              value: Number(fee.value),
            })),
            repayments: round(repayments.primary.value ?? 0, 2),
          });
          break;
        case "repaymentFrequency":
          const repaymentsValue = getRepaymentsByFrequency({
            monthlyRepayments: round(repayments.monthly.value ?? 0, 2),
            repaymentFrequency: value as RepaymentFrequency,
          });
          quoteCalculatorFormik.setValues({
            ...quoteCalculatorFormik.values,
            repayments: round(repaymentsValue ?? 0, 2),
            repaymentFrequency: value as RepaymentFrequency,
          });
          break;
        default:
          break;
      }
    }
  };

  return (
    <>
      <form onSubmit={quoteCalculatorFormik?.handleSubmit}>
        <Grid container columnSpacing={{ xs: 0, sm: 12 }} rowSpacing={{ xs: 4, sm: 0 }}>
          <Grid xs={12} sm={6}>
            <ManualQuoteFields saveQuote={saveQuote} />
          </Grid>
          <Grid xs={12} sm={6}>
            <FeeFields saveQuote={saveQuote} />
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export { QuoteCalculator };
